import React from "react";
import { AdditionalViewField, makeAdditionalViewField } from "../shared/types";
import * as S from "../shared/styles";
import { SourceColumnSelect } from "./FieldBuilder";
import FlagColumnPicker from "./FlagColumnPicker";
import { SourceColumnsState } from "./useSourceColumns";

// ----------------------------  Helpers  ----------------------------

function reorder(list: AdditionalViewField[]): AdditionalViewField[] {
  return list.map((d, i) => ({ ...d, display_order: i }));
}

// ----------------------------  DetailBuilder  ----------------------------
// The Additional View tab: what a record / object shows instead of the field rows
// where one of the flag columns picked is true. Each row is a read-only value read
// from the sample columns picked (the first with a value; a dash where none has one).

interface DetailBuilderProps {
  details: AdditionalViewField[];
  onChange: (details: AdditionalViewField[]) => void;
  // boolean sample columns that switch a record / object to the additional view
  enableFlags: string[];
  onEnableFlagsChange: (columns: string[]) => void;
  sourceColumns: SourceColumnsState;
}

const DetailBuilder: React.FC<DetailBuilderProps> = ({
  details,
  onChange,
  enableFlags,
  onEnableFlagsChange,
  sourceColumns,
}) => {
  const add = () =>
    onChange([...details, makeAdditionalViewField({ display_order: details.length })]);
  const update = (id: string, patch: Partial<AdditionalViewField>) =>
    onChange(details.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const remove = (id: string) => onChange(reorder(details.filter((d) => d.id !== id)));
  const move = (id: string, dir: number) => {
    const idx = details.findIndex((d) => d.id === id);
    const j = idx + dir;
    if (j < 0 || j >= details.length) return;
    const copy = [...details];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange(reorder(copy));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* When the additional view shows */}
      <FlagColumnPicker
        value={enableFlags}
        onChange={onEnableFlagsChange}
        sourceColumns={sourceColumns}
      />

      {details.length === 0 && (
        <p style={{ fontSize: "0.875rem", color: "#9ca3af" }}>
          No rows yet. Flagged records / objects then show only the question.
        </p>
      )}
      {details.map((d, index) => {
        const setColumn = (i: number, v: string) =>
          update(d.id, { source_columns: d.source_columns.map((c, k) => (k === i ? v : c)) });
        return (
          <div key={d.id} style={S.fieldCard}>
            <div style={S.fieldCardHeader}>
              <span />
              <div style={S.fieldCardActions}>
                <button
                  style={{ ...S.fieldCardActionBtn, opacity: index === 0 ? 0.3 : 1 }}
                  disabled={index === 0}
                  onClick={() => move(d.id, -1)}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  style={{
                    ...S.fieldCardActionBtn,
                    opacity: index === details.length - 1 ? 0.3 : 1,
                  }}
                  disabled={index === details.length - 1}
                  onClick={() => move(d.id, 1)}
                  title="Move down"
                >
                  ↓
                </button>
                <button style={S.fieldCardRemoveBtn} onClick={() => remove(d.id)} title="Remove">
                  ✕
                </button>
              </div>
            </div>

            <div style={S.fieldCardGrid}>
              <div style={S.formRow}>
                <label style={S.formLabel}>
                  Label<span style={S.formLabelRequired}>*</span>
                </label>
                <input
                  style={S.formInput}
                  value={d.label}
                  onChange={(e) => update(d.id, { label: e.target.value })}
                  placeholder="e.g. Override verdict"
                  maxLength={200}
                />
              </div>
            </div>

            <div style={S.formRow}>
              <label style={S.formLabel}>
                Source data columns<span style={S.formLabelRequired}>*</span>
              </label>
              {d.source_columns.map((col, i) => (
                <div key={i} style={S.optionRow}>
                  <SourceColumnSelect
                    value={col}
                    columns={sourceColumns.columns}
                    taken={d.source_columns.filter((_, k) => k !== i)}
                    onChange={(v) => setColumn(i, v)}
                    ariaLabel={"Source data column " + (i + 1) + " of row " + (index + 1)}
                  />
                  <button
                    style={S.fieldCardRemoveBtn}
                    onClick={() =>
                      update(d.id, { source_columns: d.source_columns.filter((_, k) => k !== i) })
                    }
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                style={S.addOptionLink}
                onClick={() => update(d.id, { source_columns: [...d.source_columns, ""] })}
                disabled={sourceColumns.loading || sourceColumns.error}
              >
                + Add column
              </button>
              <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "2px" }}>
                Shows the value of the first column that has one; a dash where none has a value.
              </div>
            </div>
          </div>
        );
      })}
      <div>
        <button style={S.addOptionLink} onClick={add}>
          + Add row
        </button>
      </div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

export default DetailBuilder;
